import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import {
  createBlobSync, mergeCollection, mergeTombstones, pruneTombstones, tombstone, sameRecords,
  matchesCloud, sameTombstones,
  type Tombstones,
} from '../lib/syncBlob';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import type { PossessionCategory } from '../constants/possessions';

/** A recurring upkeep task on a possession (Ecological EC4). */
export interface MaintenanceTask {
  id: string;
  name: string;
  /** Days between upkeep; 0 = ad-hoc (no schedule). */
  intervalDays: number;
  lastCompletedAt: string | null;
  /** How many times completed — the XP source (utils/possessionsXp). */
  completionCount: number;
}

export interface Possession {
  id: string;
  name: string;
  category: PossessionCategory;
  notes?: string;
  maintenance: MaintenanceTask[];
  createdAt: string;
  updatedAt: string;
}

// ─── Supabase sync — possessions as a blob per user (whole-store-blob pattern) ──
const USER_POSSESSIONS_TABLE = 'user_possessions';
function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' ? (raw as Tombstones) : {};
}

function readCloudPossessions(data: any) {
  return Array.isArray(data?.possessions) ? data.possessions.map(mapPossession) : [];
}

// ⚠️ Read-modify-write, NOT a wholesale upsert. The old code PUT the entire local
// array, so a device holding a stale cache silently deleted records it had never
// seen (diagnosed 2026-07-27 on the inbox — see lib/syncBlob.ts).
const blobSync = createBlobSync({
  transport: supabaseBlobTransport(USER_POSSESSIONS_TABLE),
  owner: () => usePossessionsStore.getState().cachedUserId,
  onError: err => console.error('Failed to sync possessions to Supabase:', err),
  build: (cloud) => {
    const state = usePossessionsStore.getState();
    const cloudTombs = readTombstones(cloud);
    const deleted = pruneTombstones(mergeTombstones(state.deleted, cloudTombs));
    const possessions = mergeCollection(state.possessions, readCloudPossessions(cloud), deleted);
    if (matchesCloud([[possessions, cloud?.possessions]], deleted, cloudTombs)) return null;
    return { possessions, deleted };
  },
});

interface PossessionsState {
  possessions: Possession[];
  /** id → deletedAt. Without this, the union-on-write would resurrect deletes. */
  deleted: Tombstones;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  addPossession: (input: { name: string; category: PossessionCategory; notes?: string }) => Possession;
  updatePossession: (id: string, updates: Partial<Pick<Possession, 'name' | 'category' | 'notes'>>) => void;
  deletePossession: (id: string) => void;
  addMaintenance: (possessionId: string, input: { name: string; intervalDays: number }) => void;
  updateMaintenance: (possessionId: string, taskId: string, updates: Partial<Pick<MaintenanceTask, 'name' | 'intervalDays'>>) => void;
  deleteMaintenance: (possessionId: string, taskId: string) => void;
  /** Mark a maintenance task done now — bumps its count (the XP source) + timestamp. */
  completeMaintenance: (possessionId: string, taskId: string) => void;
}

const now = () => new Date().toISOString();

function mapPossession(p: any): Possession {
  return {
    id: p.id,
    name: p.name,
    category: p.category,
    notes: p.notes,
    maintenance: Array.isArray(p.maintenance)
      ? p.maintenance.map((m: any) => ({
          id: m.id,
          name: m.name,
          intervalDays: m.intervalDays ?? 0,
          lastCompletedAt: m.lastCompletedAt ?? null,
          completionCount: m.completionCount ?? 0,
        }))
      : [],
    createdAt: p.createdAt ?? now(),
    updatedAt: p.updatedAt ?? now(),
  };
}

export const usePossessionsStore = create<PossessionsState>()(
  persist<PossessionsState, [], [], any>(
    (set, get) => ({
      possessions: [],
      deleted: {},
      initializedUserId: null,
      cachedUserId: null,

      initialize: () => dedupeInit('user_possessions', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          if (get().cachedUserId === user.id && isCloudFresh('user_possessions', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }
          const { data, error } = await supabase.from(USER_POSSESSIONS_TABLE).select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;
          const row = (data ?? [])[0];
          set((state) => {
            const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
            if (row && row.data && Array.isArray(row.data.possessions)) {
              const deleted = pruneTombstones(mergeTombstones(
                cacheIsOurs ? state.deleted : {}, readTombstones(row.data)));
              const merged = mergeCollection(
                cacheIsOurs ? state.possessions : [], readCloudPossessions(row.data), deleted);
              return {
                // Same reference when the merge was a no-op, so the subscription
                // below doesn't fire a pointless write on every boot.
                possessions: sameRecords(merged, state.possessions) ? state.possessions : merged,
                deleted: sameTombstones(deleted, state.deleted) ? state.deleted : deleted,
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }
            return cacheIsOurs
              ? { initializedUserId: user.id, cachedUserId: user.id }
              : { possessions: [], deleted: {}, initializedUserId: user.id, cachedUserId: user.id };
          });
          blobSync.writeBack(row?.data);
          markCloudFresh('user_possessions', user.id);
        } catch (error) {
          console.error('Failed to initialize possessions from Supabase:', error);
        }
      }),

      addPossession: ({ name, category, notes }) => {
        const possession: Possession = {
          id: crypto.randomUUID(),
          name: name.trim(),
          category,
          notes: notes?.trim() || undefined,
          maintenance: [],
          createdAt: now(),
          updatedAt: now(),
        };
        set(state => ({ possessions: [...state.possessions, possession] }));
        return possession;
      },

      updatePossession: (id, updates) => {
        set(state => ({
          possessions: state.possessions.map(p =>
            p.id === id ? { ...p, ...updates, updatedAt: now() } : p,
          ),
        }));
      },

      deletePossession: (id) => {
        // ⚠️ Must leave a tombstone — writes are a union now, so a bare filter
        // would be undone by the next merge against the cloud copy.
        set(state => ({
          possessions: state.possessions.filter(p => p.id !== id),
          deleted: tombstone(state.deleted, id),
        }));
      },

      addMaintenance: (possessionId, { name, intervalDays }) => {
        const task: MaintenanceTask = {
          id: crypto.randomUUID(),
          name: name.trim(),
          intervalDays,
          lastCompletedAt: null,
          completionCount: 0,
        };
        set(state => ({
          possessions: state.possessions.map(p =>
            p.id === possessionId
              ? { ...p, maintenance: [...p.maintenance, task], updatedAt: now() }
              : p,
          ),
        }));
      },

      updateMaintenance: (possessionId, taskId, updates) => {
        set(state => ({
          possessions: state.possessions.map(p =>
            p.id === possessionId
              ? {
                  ...p,
                  maintenance: p.maintenance.map(m => (m.id === taskId ? { ...m, ...updates } : m)),
                  updatedAt: now(),
                }
              : p,
          ),
        }));
      },

      deleteMaintenance: (possessionId, taskId) => {
        set(state => ({
          possessions: state.possessions.map(p =>
            p.id === possessionId
              ? { ...p, maintenance: p.maintenance.filter(m => m.id !== taskId), updatedAt: now() }
              : p,
          ),
        }));
      },

      completeMaintenance: (possessionId, taskId) => {
        set(state => ({
          possessions: state.possessions.map(p =>
            p.id === possessionId
              ? {
                  ...p,
                  maintenance: p.maintenance.map(m =>
                    m.id === taskId
                      ? { ...m, lastCompletedAt: now(), completionCount: (m.completionCount || 0) + 1 }
                      : m,
                  ),
                  updatedAt: now(),
                }
              : p,
          ),
        }));
      },
    }),
    {
      name: 'possessions-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      partialize: (state: PossessionsState) =>
        ({ possessions: state.possessions, deleted: state.deleted, cachedUserId: state.cachedUserId }) as any,
    },
  ),
);

// Auto-sync on change. No suppression flag: initialize() returns the SAME array
// reference when its merge changed nothing, and when it did change something a
// write-back is exactly what makes the two sides converge.
usePossessionsStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (state.possessions === prev.possessions && state.deleted === prev.deleted) return;
  blobSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(usePossessionsStore);
